import {
  useCompany,
  useGetCompanyAttributeOptions,
  useGetTaskGroupOptions,
  useGetTaskTemplate,
  usePermissions,
  useUpsertTaskTemplate,
  useGetCompanyRoleOptions,
} from '@dispatch/shared/hooks';
import {
  getValuesForSubmit,
  parseApiData,
  TaskTemplatesDetails,
} from '@dispatch/web/features/task-templates';
import {
  TaskTemplateForm,
  TaskTemplatesPostBody,
} from '@dispatch/shared/types';
import { useNotificationMutationFactory } from '@dispatch/web/ui';
import React from 'react';
import { useOutletContext, useParams } from 'react-router-dom';
import { useCurrentUser } from '@dispatch/web/auth';

const TaskTemplateDetailsPage = () => {
  const { permissions } = usePermissions();
  const { tenantId } = useCurrentUser();
  const outletContext: {
    refetchList: () => Promise<void>;
  } = useOutletContext();
  const params = useParams();
  const taskTemplateId = params['taskTemplateId'] || '';
  const { company, perilOptions: perils } = useCompany();
  const { companyAttributes } = useGetCompanyAttributeOptions(
    company?.attributeDefinition || []
  );
  const { taskGroupOptions, taskGroupOptionsStatus } = useGetTaskGroupOptions();
  const { companyRoles } = useGetCompanyRoleOptions(company?.role || []);
  const { data: taskTemplate, status } = useGetTaskTemplate(taskTemplateId);

  const { notifySuccess } = useNotificationMutationFactory(
    'Task Template',
    'update'
  );
  const onSuccess = () => {
    notifySuccess();
    outletContext?.refetchList?.();
  };

  const { upsertTaskTemplate, upsertTaskTemplateStatus } =
    useUpsertTaskTemplate(
      onSuccess,
      tenantId || '',
      taskTemplate?.data.id,
      taskTemplateId
    );

  const submitData = (values: TaskTemplateForm) => {
    const output: TaskTemplatesPostBody = getValuesForSubmit(values);

    const postBody: TaskTemplatesPostBody & { id: string; tenantId: string } = {
      ...output,
      id: taskTemplateId,
      tenantId: tenantId || '',
    };

    upsertTaskTemplate(postBody);
  };

  const handleStatuses = () => {
    if (
      status === 'loading' ||
      taskGroupOptionsStatus === 'loading' ||
      upsertTaskTemplateStatus === 'loading'
    ) {
      return 'loading';
    }
    return 'success';
  };

  const handleDeactivate = (values: TaskTemplateForm) => {
    if (!values) return;

    const postBody = {
      ...values,
      id: taskTemplateId,
      deactivatedAt: new Date().toISOString(),
    };

    upsertTaskTemplate(postBody);
  };

  const handleActivate = (values: TaskTemplateForm) => {
    if (!values) return;

    const postBody = {
      ...values,
      id: taskTemplateId,
      deactivatedAt: null,
    };

    upsertTaskTemplate(postBody);
  };

  const parsedApiData: TaskTemplateForm | undefined = React.useMemo(() => {
    if (!taskTemplate?.data) return undefined;
    return parseApiData(taskTemplate?.data);
  }, [taskTemplate?.data]);

  return (
    <TaskTemplatesDetails
      onSubmit={submitData}
      onDeactivate={handleDeactivate}
      onActivate={handleActivate}
      attributeDefinitions={companyAttributes || []}
      companyUserRoleOptions={companyRoles || []}
      taskGroupOptions={taskGroupOptions || []}
      perilOptions={perils}
      data={parsedApiData}
      status={handleStatuses()}
      permissions={permissions}
    />
  );
};

export default TaskTemplateDetailsPage;
